import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessage,
    ValidationMessageType,
    SettingState,
    TibboDataType,
    SettingOption,
} from '@appblocks/codegeneration/types';
import { TIBBO_DEVICE_ID } from '@appblocks/platforms/PlatformUtils';
import BaseFeature from '@appblocks/codegeneration/BaseFeature';

export default class WirelessModule extends BaseFeature {
    constructor() {
        super();
        this.name = 'Wireless Module';
        this.id = 'wireless_module';
        this.icon = 'FaWifi';
        this.priority = 99;
        this.requirements = [['TPP', 'tios']];
        this.capabilities = ['bluetooth'];
        this.devices = [
            TIBBO_DEVICE_ID.TPP2WG2,
            TIBBO_DEVICE_ID.TPP3WG2,
            TIBBO_DEVICE_ID.EM2000W,
            TIBBO_DEVICE_ID.EM2000,
            TIBBO_DEVICE_ID.EM510W,
        ];
        const settings = [
            {
                id: 'ENABLED',
                name: 'Enabled',
                settingType: SettingType.BOOLEAN,
                default: '1',
                readonly: true,
            },
            {
                id: 'MODULE',
                name: 'Module',
                settingType: SettingType.OPTION,
                required: true,
                default: 'WA2000C',
            },
        ];


        this.settings = this.settings.concat(settings);
    }

    validate(): Array<ValidationMessage> {
        const errors: Array<ValidationMessage> = [];
        const { project } = this;
        const featureId = this.getID();
        const tibbit37 = project.getFeature('tibbit_37');

        if (!this.getEnabled()) {
            return errors;
        }

        const MODULE = this.getSettingValue('MODULE');

        if (MODULE === '') {
            errors.push({
                featureId,
                settingId: 'MODULE',
                message: 'No Wi-Fi module selected',
                messageType: ValidationMessageType.ERROR,
            });
        }

        if (MODULE === 'WA2000U' && tibbit37 === undefined) {
            errors.push({
                featureId,
                settingId: 'MODULE',
                message: 'RF Connector (Tibbit #37) is required for WA2000U.',
                messageType: ValidationMessageType.ERROR,
            });
        }

        if (MODULE === 'WA2000C' && tibbit37 !== undefined) {
            errors.push({
                featureId,
                settingId: 'MODULE',
                message: 'RF Connector (Tibbit #37) is not required for WA2000C and might cause issues.',
                messageType: ValidationMessageType.ERROR,
            });
        }

        // project.addNewNode('node_system', 'boot', 'node_wifi', 'wifi_connect');
        return errors;
    }

    getDependencies() {
        const dependencies = [
            'wln',
        ];

        return dependencies;
    }

    getDefines(): Array<Define> {
        let defines = [
            {
                name: 'WLN_DEBUG_PRINT',
                value: '1',
            },
            {
                name: 'MAX_NUM_WLN_FAILURES',
                value: '5',
            },
        ];

        if (this.project.device.id === 'EM510W') {
            defines = defines.concat([{
                name: 'WLN_CLK',
                value: 'PL_IO_NUM_7',
            },
            {
                name: 'WLN_CS',
                value: 'PL_IO_NUM_8',
            },
            {
                name: 'WLN_DI',
                value: 'PL_IO_NUM_6',
            },
            {
                name: 'WLN_DO',
                value: 'PL_IO_NUM_6',
            }]);
        } else if (this.project.device.id === 'DS1102N' || this.project.device.id === 'DS1101N') {
            defines = defines.concat([{
                name: 'WLN_CLK',
                value: 'PL_IO_NUM_14',
            },
            {
                name: 'WLN_CS',
                value: 'PL_IO_NUM_15',
            },
            {
                name: 'WLN_DI',
                value: 'PL_IO_NUM_12',
            },
            {
                name: 'WLN_DO',
                value: 'PL_IO_NUM_13',
            },
            {
                name: 'WLN_RST',
                value: 'PL_IO_NUM_11',
            }]);
        } else {
            defines = defines.concat([{
                name: 'WLN_CLK',
                value: 'PL_IO_NUM_53',
            },
            {
                name: 'WLN_CS',
                value: 'PL_IO_NUM_49',
            },
            {
                name: 'WLN_DI',
                value: 'PL_IO_NUM_52',
            },
            {
                name: 'WLN_DO',
                value: 'PL_IO_NUM_50',
            },
            {
                name: 'WLN_RST',
                value: 'PL_IO_NUM_51',
            }]);
        }

        if ([
            'TPP2W(G2)',
            'TPP3W(G2)',
            'TPP2W(G3)',
            'TPP3W(G3)',
        ].includes(this.project.device.id)) {
            defines = defines.concat([{
                name: 'SIGNAL_STRENGTH_INDICATION',
                value: '1',
            }]);
        }

        if ([
            'DS1102N',
            'DS1101N',
        ].includes(this.project.device.id)) {
            defines = defines.concat([{
                name: 'SIGNAL_STRENGTH_INDICATION',
                value: '2',
            }]);
        }

        return defines;
    }

    getSettingOptions(settingId: string): SettingOption[] | undefined {
        const { project } = this;
        if (settingId === 'MODULE') {
            switch (project.device.id) {
                case TIBBO_DEVICE_ID.LTPP3G2:
                    return [{
                        name: 'WA2000C',
                        value: 'WA2000C',
                    },
                    {
                        name: 'WA2000U',
                        value: 'WA2000U',
                    }];
                default:
                    return [{
                        name: 'WA2000C',
                        value: 'WA2000C',
                    },
                    {
                        name: 'WA2000U',
                        value: 'WA2000U',
                    }];
            }
        }
        return super.getSettingOptions(settingId);
    }

    getCodeBlocks() {
        const blocks: Array<CodeBlock> = [];

        const { project } = this;

        if (project.runtime === 'simulator') {
            return blocks;
        }

        if (this.project.device.id === 'DS1102N' || this.project.device.id === 'DS1101N') {
            blocks.push({
                location: 'PROJECT_DEFINES',
                blockType: BlockType.CODE,
                id: '/dOBJECT_WLN=WLN_ENABLED ',
                template: '/dOBJECT_WLN=WLN_ENABLED ',
            });
        }

        let required = false;
        for (let i = 0; i < project.features.length; i++) {
            const dependencies = project.features[i].getDependencies();
            if (dependencies.includes('wireless_module') && project.features[i].getEnabled()) {
                required = true;
                break;
            }
        }

        if (!required) {
            return blocks;
        }

        let bootCode = '';

        bootCode += `
    'Wireless Module Setup
    '================================================================
    wireless_enabled=NO
    wln.csmap=WLN_CS
    io.num=WLN_CS
    io.enabled=YES
    wln.dimap=WLN_DI
    wln.domap=WLN_DO
    io.num=WLN_DO
    io.enabled=YES
    wln.clkmap=WLN_CLK
    io.num=WLN_CLK
    io.enabled=YES
    io.num=WLN_RST
    io.enabled=YES
    io.num=WLN_RST
    io.state=LOW
    dim wln_delay as dword = sys.timercountms + 2
    while sys.timercountms < wln_delay
    wend
    io.state=HIGH
    wln.getmoduletype()
    wln.buffrq(5)
    sys.buffalloc()
    if wln.boot(0)<>OK then
        sys.debugprint("wireless module boot failed\\r\\n")
        wireless_enabled=NO
    else
        sys.debugprint("Wireless F/W version:"+wln.fwversion+"\\r\\n")
    end if
    '-----------------------------------------------------------------`;

        blocks.push({
            location: 'boot',
            blockType: BlockType.CODE,
            template: bootCode,
        });

        blocks.push({
            location: 'GLOBAL_DECLARATIONS',
            blockType: BlockType.CODE,
            template: `
declare sub wln_reset_module()
declare wireless_enabled as no_yes
`,
        });

        blocks.push({
            location: 'GLOBAL_DIMS',
            blockType: BlockType.CODE,
            template: `
dim wireless_enabled as no_yes
`,
        });

        blocks.push({
            location: 'DEVICE_TBS',
            blockType: BlockType.CODE,
            template: `
sub wln_reset_module()
    wln.disable()
    io.num=WLN_RST
    io.state=LOW
    dim wln_delay as dword = sys.timercountms + 2
    while sys.timercountms < wln_delay
    wend
    io.state=HIGH
    wln.getmoduletype()
    if wln.boot(0)<>OK then
        sys.debugprint("wireless module boot failed\\r\\n")
    else
        sys.debugprint("Wireless F/W version:"+wln.fwversion+"\\r\\n")
    end if
end sub`,
        });

        return blocks;
    }

    getDetails(): string {
        return 'https://docs.tibbo.com/wa2000';
    }
}
